import { orderBy } from 'lodash';

import { MemosTable_MemoFragment } from '@/modules/memos/MemosTable/graphql/MemosTable.generated';

import { DiagramArtifactsQuery } from '../../graphql/DiagramArtifacts.generated';

/** The kind of artifact in a row. The variant sets how the row opens. */
export enum ArtifactRowVariant {
  Diagram = 'diagram',
  Memo = 'memo',
}

const ARTIFACT_ROW_VARIANT_LABELS: Record<ArtifactRowVariant, string> = {
  [ArtifactRowVariant.Diagram]: 'Diagram',
  [ArtifactRowVariant.Memo]: 'Text',
};

/** One row of the artifacts table. Diagrams and memos share this shape. */
export interface ArtifactRowData {
  id: string;
  variant: ArtifactRowVariant;
  name: string;
  type: string;
  createdBy: string;
  updatedAt: Date;
  isGenerating: boolean;
}

type DiagramArtifact = NonNullable<
  NonNullable<
    NonNullable<DiagramArtifactsQuery['diagramArtifacts']['edges']>[number]
  >['node']
>;

interface ToArtifactRowsParams {
  diagrams: DiagramArtifact[];
  memos: MemosTable_MemoFragment[];
  generatingDiagramIds: Set<string>;
  generatingMemoIds: Set<string>;
}

/**
 * Normalizes diagrams and memos into table rows, with the most recently
 * modified artifact first.
 */
export function toArtifactRows({
  diagrams,
  memos,
  generatingDiagramIds,
  generatingMemoIds,
}: ToArtifactRowsParams): ArtifactRowData[] {
  const diagramRows = diagrams.map((diagram) => ({
    id: diagram.id,
    variant: ArtifactRowVariant.Diagram,
    name: diagram.name,
    type: ARTIFACT_ROW_VARIANT_LABELS[ArtifactRowVariant.Diagram],
    createdBy: diagram.user.displayName,
    updatedAt: diagram.updatedAt,
    isGenerating: generatingDiagramIds.has(diagram.id),
  }));
  const memoRows = memos.map((memo) => ({
    id: memo.id,
    variant: ArtifactRowVariant.Memo,
    name: memo.name,
    type: ARTIFACT_ROW_VARIANT_LABELS[ArtifactRowVariant.Memo],
    createdBy: memo.user.displayName,
    updatedAt: memo.updatedAt,
    isGenerating: generatingMemoIds.has(memo.id),
  }));

  return orderBy(
    [...diagramRows, ...memoRows],
    (row) => new Date(row.updatedAt).getTime(),
    'desc'
  );
}
